import { Slot } from '@radix-ui/react-slot';
import {
  Children,
  cloneElement,
  isValidElement,
  type ComponentProps,
  type ReactNode,
} from 'react';

import { cn } from '@/lib/utils';

import { SETTINGS_ROW_ICON_TONE } from './types/settings-list-variants-enums';



function SettingsList({ className, ...props }: ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="settings-list"
      className={cn(
        'divide-y divide-border-default overflow-hidden rounded-surface border border-border-default bg-surface-base',
        className
      )}
      {...props}
    />
  );
}

interface SettingsRowIconProps extends ComponentProps<'div'> {
  tone?: SETTINGS_ROW_ICON_TONE;
}

function SettingsRowIcon({
  tone = SETTINGS_ROW_ICON_TONE.DEFAULT,
  className,
  ...props
}: SettingsRowIconProps) {
  return (
    <div
      data-slot="settings-row-icon"
      data-tone={tone}
      className={cn(
        'flex size-settings-row-leading-touch items-center justify-center rounded-control border sm:size-settings-row-leading',
        tone === SETTINGS_ROW_ICON_TONE.PRIMARY &&
          'border-border-default bg-surface-raised text-content-link shadow-surface',
        tone === SETTINGS_ROW_ICON_TONE.DEFAULT &&
          'border-border-default bg-surface-base text-content-secondary',
        className
      )}
      {...props}
    />
  );
}

export interface SettingsRowProps extends Omit<ComponentProps<'li'>, 'title'> {
  leading?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  trailing?: ReactNode;
  asChild?: boolean;
}

function SettingsRowInner({
  leading,
  title,
  description,
  trailing,
  children,
}: {
  leading?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  trailing?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <>
      {leading ? (
        <div
          data-slot="settings-row-leading"
          className="flex shrink-0 items-center justify-center text-content-secondary"
        >
          {leading}
        </div>
      ) : null}

      <div data-slot="settings-row-body" className="min-w-0 flex-1 space-y-0.5">
        <div
          className={cn(
            'font-medium text-content-primary',
            'type-settings-row-title'
          )}
        >
          {title}
        </div>
        {description ? (
          <div
            className={cn(
              'font-normal text-content-secondary',
              'type-settings-row-description'
            )}
          >
            {description}
          </div>
        ) : null}
        {children}
      </div>

      {trailing ? (
        <div
          data-slot="settings-row-trailing"
          className="flex shrink-0 items-center gap-2 text-sm text-content-tertiary"
        >
          {trailing}
        </div>
      ) : null}
    </>
  );
}

function SettingsRow({
  leading,
  title,
  description,
  trailing,
  asChild = false,
  className,
  children,
  ...props
}: SettingsRowProps) {
  const hasDescription = Boolean(description);
  // Title-only rows are 56px (min-h-14); described rows ~72px — both exceed the
  // ChoiceRow 40/44 floor used for bare Checkbox/Radio/Switch compositions.
  const rowClassName = cn(
    'flex w-full items-center gap-3 px-4 text-left outline-none transition-colors',
    hasDescription
      ? 'min-h-[var(--density-settings-row-described)] py-3'
      : 'min-h-[var(--density-settings-row)] py-2.5',
    'focus-visible:bg-neutral-interaction-hover focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus-ring',
    asChild &&
      'active:bg-neutral-interaction-pressed hover:bg-neutral-interaction-hover',
    className
  );

  const inner = (
    <SettingsRowInner
      leading={leading}
      title={title}
      description={description}
      trailing={trailing}
    />
  );

  if (asChild) {
    const child = Children.only(children);
    if (!isValidElement(child)) {
      return null;
    }

    return (
      <Slot
        data-slot="settings-row"
        className={rowClassName}
        {...props}
      >
        {cloneElement(child, undefined, inner)}
      </Slot>
    );
  }

  return (
    <li
      data-slot="settings-row"
      className={rowClassName}
      {...props}
    >
      {inner}
      {children}
    </li>
  );
}

export { SettingsList, SettingsRow, SettingsRowIcon };
